import { useMemo, useState } from 'react';
import { api, ago, humanise } from '../api';
import { PageHead, Panel, Table, Toast, useDebounced, useLoad } from '../ui';

/**
 * Deleting test accounts — with their routes, rides, trips, chats, documents
 * and support conversations. Staff accounts are not listed. Irreversible, so
 * the admin ticks exactly what goes and types DELETE.
 */
export function Accounts() {
  const [q, setQ] = useState('');
  const search = useDebounced(q);
  const { data, error, reload } = useLoad(() => api.accounts(search), { deps: [search] });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; bad?: boolean } | null>(null);

  const rows = data ?? [];
  const allOn = rows.length > 0 && rows.every((r) => picked.has(r.id));
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selected = useMemo(() => rows.filter((r) => picked.has(r.id)), [rows, picked]);

  const purge = async () => {
    setBusy(true);
    try {
      const r = await api.purgeAccounts([...picked]);
      setToast({ msg: `Deleted ${r.deleted.users} account(s), ${r.deleted.trips} trip(s), ${r.deleted.rides} ride(s).` });
      setPicked(new Set());
      setTyped('');
      await reload();
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'Could not delete', bad: true });
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 5000);
    }
  };

  return (
    <>
      <PageHead
        title="Clean up accounts"
        blurb="Delete test and dummy accounts with everything attached to them: routes, rides, trips, chats, documents and support conversations. Staff accounts are never listed. This cannot be undone."
      />
      <Panel
        title={`${rows.length} accounts${picked.size ? ` · ${picked.size} selected` : ''}`}
        right={
          <>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, phone, email…" style={{ width: 200 }} />
            <button className="btn sm" onClick={() => setPicked(allOn ? new Set() : new Set(rows.map((r) => r.id)))}>
              {allOn ? 'Clear selection' : 'Select all shown'}
            </button>
          </>
        }
      >
        {error ? <div className="empty">{error}</div> : (
          <Table
            rows={data}
            keyOf={(r) => r.id}
            onRow={(r) => toggle(r.id)}
            empty={q ? 'Nobody matches that.' : 'No accounts besides staff.'}
            columns={[
              { head: '', shrink: true, cell: (r) => <input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)} onClick={(e) => e.stopPropagation()} /> },
              { head: 'Account', cell: (r) => (<><div>{r.full_name ?? <span className="muted">no name</span>}</div><div className="mono muted">{r.phone ?? r.email ?? '—'}</div></>) },
              { head: 'Role', shrink: true, cell: (r) => humanise(r.role) },
              { head: 'Routes', num: true, shrink: true, cell: (r) => r.routes },
              { head: 'Trips', num: true, shrink: true, cell: (r) => r.trips },
              { head: 'Joined', shrink: true, cell: (r) => ago(r.created_at) },
            ]}
          />
        )}
      </Panel>

      {picked.size ? (
        <Panel title="Delete the selected accounts">
          <div className="pad">
            <p className="hint bad">
              {selected.length} account(s) and everything attached to them will be permanently deleted:
              {' '}{selected.slice(0, 6).map((r) => r.full_name ?? r.phone).join(', ')}{selected.length > 6 ? ` and ${selected.length - 6} more` : ''}.
            </p>
            <form className="row" onSubmit={(e) => { e.preventDefault(); if (typed === 'DELETE') void purge(); }}>
              <input type="text" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type DELETE to confirm" />
              <button className="btn danger" disabled={busy || typed !== 'DELETE'}>{busy ? 'Deleting…' : `Delete ${picked.size}`}</button>
            </form>
          </div>
        </Panel>
      ) : null}
      {toast ? <Toast msg={toast.msg} bad={toast.bad} /> : null}
    </>
  );
}
